/**
 * SHO-209 — Collection & Search infinite scroll.
 *
 * Progressive enhancement over numbered pagination. Enabled via the global
 * "Pagination style: Infinite scroll" setting, which renders a sentinel
 * ([data-infinite-scroll]) in place of the numbered pagination (see
 * snippets/collection-infinite-scroll.liquid).
 *
 * For each sentinel we fetch the next page through the Section Rendering API
 * (?section_id=…&page=N&<current filters/sort>), parse the returned grid, and
 * append its children to the live grid. An IntersectionObserver auto-loads as
 * the shopper nears the bottom; a "Load more" button is the click/keyboard
 * fallback.
 *
 * The filter/sort flow (theme.AjaxRenderer) replaces #CollectionAjaxContent's
 * innerHTML — which does not re-run inline scripts — so this global script
 * re-scans on DOM changes (MutationObserver) and re-initialises the fresh
 * sentinel, resetting paging to the new result set.
 */
(function () {
  'use strict';

  var INIT_ATTR = 'data-infinite-scroll-init';

  function reinitProducts(grid) {
    try {
      if (window.theme && theme.QuickAdd) new theme.QuickAdd(grid);
    } catch (e) {}
    try {
      if (window.theme && theme.QuickShop) new theme.QuickShop(grid);
    } catch (e) {}
    try {
      if (window.yotpoWidgetsContainer && yotpoWidgetsContainer.initWidgets) {
        yotpoWidgetsContainer.initWidgets();
      }
    } catch (e) {}
    // Let other theme scripts react to newly appended products.
    document.dispatchEvent(new CustomEvent('collection:appended', { detail: { grid: grid } }));
  }

  function setupSentinel(sentinel) {
    if (sentinel.getAttribute(INIT_ATTR) === 'true') return;
    sentinel.setAttribute(INIT_ATTR, 'true');

    var sectionId = sentinel.getAttribute('data-section-id');
    var gridSelector = sentinel.getAttribute('data-grid-selector') || '.new-grid.product-grid.collection-grid';
    var totalPages = parseInt(sentinel.getAttribute('data-total-pages'), 10) || 1;
    var currentPage = parseInt(sentinel.getAttribute('data-current-page'), 10) || 1;

    // Prefer a grid inside the same ajax content wrapper; fall back to the first on the page.
    var scope = sentinel.closest('#CollectionAjaxContent') || document;
    var grid = scope.querySelector(gridSelector) || document.querySelector(gridSelector);
    if (!grid || !sectionId) return;

    var moreBtn = sentinel.querySelector('[data-infinite-scroll-more]');
    var statusEl = sentinel.querySelector('[data-infinite-scroll-status]');
    var busy = false;
    var done = currentPage >= totalPages;

    function finish() {
      done = true;
      sentinel.classList.add('is-done');
      sentinel.classList.remove('is-loading');
      if (observer) observer.disconnect();
    }

    function loadNext() {
      if (busy || done) return;
      busy = true;
      sentinel.classList.add('is-loading');
      if (statusEl) statusEl.textContent = 'Loading…';

      var nextPage = currentPage + 1;
      var params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      params.set('section_id', sectionId);
      params.set('page', String(nextPage));
      var url = window.location.pathname + '?' + params.toString();

      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector(gridSelector);
          var appended = 0;
          if (newGrid) {
            var nodes = Array.prototype.slice.call(newGrid.children);
            nodes.forEach(function (node) {
              grid.appendChild(node);
              appended++;
            });
          }
          currentPage = nextPage;
          sentinel.setAttribute('data-current-page', String(currentPage));
          busy = false;
          sentinel.classList.remove('is-loading');
          if (statusEl) statusEl.textContent = '';
          if (appended > 0) reinitProducts(grid);
          if (currentPage >= totalPages) finish();
        })
        .catch(function () {
          busy = false;
          sentinel.classList.remove('is-loading');
          if (statusEl) statusEl.textContent = '';
          // Leave the button so the shopper can retry.
        });
    }

    if (moreBtn) moreBtn.addEventListener('click', loadNext);

    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { loadNext(); break; }
        }
      }, { rootMargin: '600px 0px' });
      observer.observe(sentinel);
    }

    if (done) finish();
  }

  function scan(root) {
    var sentinels = (root || document).querySelectorAll('[data-infinite-scroll]');
    for (var i = 0; i < sentinels.length; i++) setupSentinel(sentinels[i]);
  }

  // Re-scan when the filter/sort AJAX replaces the collection/search content,
  // so the fresh sentinel (new result set, page 1) is re-initialised.
  function observeContainers() {
    var containers = document.querySelectorAll('#CollectionAjaxContent');
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      if (c.__infiniteScrollObserved) continue;
      c.__infiniteScrollObserved = true;
      var mo = new MutationObserver(function () { scan(document); });
      mo.observe(c, { childList: true, subtree: true });
    }
  }

  function init() {
    scan(document);
    observeContainers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
